import { Injectable } from '@angular/core'
import { Actions, createEffect, ofType } from '@ngrx/effects'
import { MemoizedProjection, select, Store } from '@ngrx/store'
import { of } from 'rxjs'
import { catchError, debounceTime, filter, map, mergeMap, switchMap, tap, withLatestFrom } from 'rxjs/operators'
import { ErrorReportingService } from '../../../shared/services/error-reporting.service'
import { PayloadedAction } from '../../../shared/types'
import { generateVehicleSlug, geoLocationToAAALocation } from '../../../shared/utils'
import { AAAStore } from '../../../store/root-reducer'
import { openMessageDialog } from '../../ui/ui.actions';
import { MessageDialogTypes, StepTypes, TowSections } from '../../ui/ui.types';
import { selectMemberActiveVehicle, selectMemberShipLevel } from '../../member/member.selectors'
import { LocationFacadeService } from '../service/location-facade.service'
import {
  ADDRESS_LOOKUP,
  CHECK_TOW_DISTANCE,
  checkTowDistance,
  completeAddressLookup,
  completeSetTowDestination,
  CONFIRM_TOW_DISTANCE,
  GET_TOW_DESTINATION_COORDINATES,
  NON_AAR_TOWING_NAMES,
  notifyAddressLookupFailure,
  notifySetTowDestinationFailure,
  requestSetTowDestination,
  SET_AAR_ADDRESS,
  SET_TOW_DESTINATION,
  SET_TOWING_STEP,
  setAARAddress,
  setTowDestinationMarker,
  setTowingStep,
} from './tow-location.actions'
import { TaggingService } from '../../tagging/tagging.service'
import {
  selectIsTowLocationPreviewValid,
  selectIsTowLocationValid,
  selectTowLocationPreview
} from './tow-location.selectors'
import { Params, Router } from '@angular/router'
import {
  AddressWithLandmark,
  GenericCoordinates,
  GeoLocationMarker,
  TowLocation
} from '../location.types'
import { haversine } from '../../../shared/haversine'
import { calculateTowingDistance, isMotorhomeRV } from './tow-location.utils'
import {
  selectClubTowSettingByMemberLevel,
  selectHasTowClubSettings,
  selectTowAlertRV
} from '../../servicing-club/servicing-club.selectors';
import { addPartialCallRequest } from '../../dashboard/calls.actions';
import { selectBreakdownLocationCoordinates, selectBreakdownLocationDetails, selectLocationClub } from '../location.selectors';
import events from '../../tagging/events'
import { RapService } from '../../rap/rap.service'
import { selectRoutesDistance } from '../../route-distance/route-distance.selector'
import { selectMaxTowMileLimit, selectModeConfiguration } from '../../auth/auth.selectors'
import { selectIsEVstation, selectShownAARs } from '../aar/aar.selectors'
import { setTowDestinationCoordinates } from '../../dashboard/calls-statuses/call-status.actions'
import { selectActiveTowLimitRestriction, selectTowLimitAdvisories } from '../../advisories/advisories.selectors'
import { requestTowServiceAvailability } from '../../tow-service-availability/tow-service-availability.actions'
import { LocationUtils } from '../location.utils'
import { evaluateTowBenefits, isTowLimitHardBlock } from './tow-destination-gates'
import { requestSearchArea } from '../aar/aar.actions';
import { ROUTER_NAVIGATED } from '@ngrx/router-store'
import { setResetPassengers } from '../../submit/submit.actions'
import { selectActiveCallStatus } from '../../dashboard/calls-statuses/call-status.selectors'
import { MembershipLevelsSettings, Vehicle } from '../../member/member.types'
import { ServiceConfigurationSetting } from '../../servicing-club/servicing-club.types'
import { buildTitle } from '../../../shared/utils/title'
import { Title } from '@angular/platform-browser'
import { selectNeedsTow } from '../../issue/issue.selectors';
import { selectUIState } from '../../ui/ui.selectors';
import { TITLE_SUBMIT } from '../../constants/shared.constants';
import { AdobeEventService } from '../../tagging/adobe/event-adobe.service'
import { AdobeEventTypes } from '../../tagging/tagging.types'
import { createGetVehicleDetailsByModel } from '../../vehicle/vehicle.selectors'

const handleTowDestination = (destination: TowLocation) =>
  !Boolean(destination.streetName)
    ? openMessageDialog({
      payload: { type: MessageDialogTypes.TOW_LOCATION_ADDRESS }
    })
    : completeSetTowDestination({
      payload: {
        name: destination.name || NON_AAR_TOWING_NAMES.CUSTOM,
        serviceProviderCode: destination.serviceProviderCode,
        address: destination.address,
        landmark: destination.landmark,
        streetName: destination.streetName,
        streetNumber: String(destination.streetNumber),
        city: destination.city,
        state: destination.state,
        postalCode: destination.postalCode,
        accuracy: destination.accuracy,
        latitude: String(destination.latitude),
        longitude: String(destination.longitude),
        isAar: destination.isAar,
        foiType: destination.foiType,
        ...( destination.country ? { country: destination.country } : {} ),
        ...( destination.emailAddress ? { emailAddress: destination.emailAddress } : {} ),
        ...( destination.id ? { aarId: destination.id, id: destination.id } : {} )
      }
    })

@Injectable()
export class TowLocationEffects {
  constructor(
    private actions$: Actions,
    private store$: Store<AAAStore>,
    private _geocodeService: LocationFacadeService,
    private taggingService: TaggingService,
    private adobeEventService: AdobeEventService,
    private errorReportingService: ErrorReportingService,
    private router: Router,
    private rapService: RapService,
    private locationUtils: LocationUtils,
    private titleService: Title,
  ) { }

  handleNavigation = createEffect(() =>
    this.actions$.pipe(
      ofType(ROUTER_NAVIGATED),
      map(
        (action: PayloadedAction) => action.payload.routerState.root.queryParams
      ),
      filter((params: Params) => params.step === StepTypes.TOWING),
      withLatestFrom(
        this.store$.pipe(select(selectNeedsTow)),
        this.store$.pipe(select(selectUIState)),
      ),
      map(([params, needsTow, uiState]) => {
        // reset passengers
        if (!needsTow) {
          this.store$.dispatch(setResetPassengers())
        }
        const leavingPassengersPage = uiState.currentUrl.indexOf(TowSections.PASSENGERS) > -1
        const isPassengersSection = params.section === TowSections.PASSENGERS
        if (isPassengersSection) {
          this.taggingService.setPageLoadEvent({ pageType: events.passengers.PAGE_TYPE, pageName: events.passengers.PAGE_NAME_TOW_RIDERS })
        } else if(leavingPassengersPage) {
          this.taggingService.setPageLoadEvent({ pageType: events.towTo.PAGE_TYPE, pageName: events.towTo.PAGE_NAME_CONFIRM_TOW_LOCATION })
        }
        const step =
          (isPassengersSection && !needsTow
            ? StepTypes.SUBMIT
            : params.section) || null
        return setTowingStep({ payload: { step } })
      }),
    )
  )

  handleSetTowDestination = createEffect(
    () =>
      this.actions$.pipe(
        ofType(SET_TOW_DESTINATION.SUCCESS),
        withLatestFrom(this.store$.select(selectActiveCallStatus)),
        filter(([_, activeCallStatus]) => activeCallStatus === null),
        map(() =>
          this.router.navigate([], {
            queryParams: {
              step: StepTypes.TOWING,
              section: TowSections.PASSENGERS,
            },
          })
        )
      ),
    { dispatch: false }
  )

  setMarkerAsTowDestination = createEffect(() => this.actions$.pipe(
    ofType(SET_TOW_DESTINATION.REQUEST),
    filter((action: ReturnType<typeof requestSetTowDestination>) =>
      action.payload.hasOwnProperty('lat')
      && action.payload.hasOwnProperty('lng')
      && action.payload.hasOwnProperty('address')
    ),
    switchMap((action: PayloadedAction<TowLocation | GeoLocationMarker>) =>
      this._geocodeService.getLocationFromAddress(action.payload.address).pipe(
        map((addresses) => {
          const destination = geoLocationToAAALocation({ address: action.payload.address, location: addresses[0] })

          return handleTowDestination({
            address: action.payload.address,
            name: action.payload.name,
            isAar: action.payload.isAar,
            serviceProviderCode: action.payload.serviceProviderCode,
            ...destination
          })
        }),
        catchError((error) => this.errorReportingService.notifyErrorObservable(error, notifySetTowDestinationFailure))
      )
    ),
  ))

  setLocationAsTowingDestination = createEffect(() => this.actions$.pipe(
    ofType(SET_TOW_DESTINATION.REQUEST),
    filter((action: PayloadedAction<TowLocation>) =>
      action.payload.hasOwnProperty('streetName')
      && action.payload.hasOwnProperty('streetNumber')
      && action.payload.hasOwnProperty('city')
      && action.payload.hasOwnProperty('state')
      && action.payload.hasOwnProperty('postalCode')
    ),
    map((action: PayloadedAction<TowLocation>) => handleTowDestination(action.payload))
  ))

  lookupAddress = createEffect(() => this.actions$.pipe(
    ofType(ADDRESS_LOOKUP.REQUEST),
    filter((action: PayloadedAction<AddressWithLandmark>) => !!action.payload.address),
    withLatestFrom(
      this.store$.select(selectBreakdownLocationCoordinates),
    ),
    switchMap(([action, breakdownLocationCoords]: [PayloadedAction<AddressWithLandmark>, GenericCoordinates]) =>
      this._geocodeService.getLocationFromAddress(action.payload.address).pipe(
        switchMap((geoLocation) => {
          const location = geoLocationToAAALocation({
            address: action.payload.address,
            location: geoLocation[0],
            ...(action.payload.landmark ? { landmark: action.payload.landmark } : {})
          })
          return this.resolveLocationRequestActions(action, location, breakdownLocationCoords)
        }),
        catchError((error) => this.errorReportingService.notifyErrorObservable(error, notifyAddressLookupFailure))
      )
    ),
  ))

  lookupSuccess = createEffect(() => this.actions$.pipe(
    ofType(ADDRESS_LOOKUP.SUCCESS),
    map(() => {
      this.taggingService.setClickEvent(
        events.towTo.DESTINATION_MANUAL_INPUT,
        events.towTo.DESTINATION_PAGE_TYPE,
        { params: { itemName: null, itemId: null } }
      )
      this.adobeEventService.sendEvent({
        eventName: AdobeEventTypes.CTA,
        eventValue: events.towTo.DESTINATION_MANUAL_INPUT
      })
    })
  ), { dispatch: false })

  lookupFinalize = createEffect(() => this.actions$.pipe(
    ofType(CONFIRM_TOW_DISTANCE),
    withLatestFrom(
      this.store$.pipe(select(selectTowLocationPreview)),
    ),
    filter(([_, towPreview]) => !!towPreview?.location),
    map(([_, towPreview]) => {
      this.taggingService.setClickEvent(
        events.towTo.DESTINATION_NEXT_CLICK,
        events.towTo.DESTINATION_PAGE_TYPE,
        { params: { itemName: null, itemId: null } }
      )
      this.adobeEventService.sendEvent({
        eventName: AdobeEventTypes.CTA,
        eventValue: events.towTo.DESTINATION_NEXT_CLICK
      }, null, {
        location_name: towPreview.location.name,
        location_code: towPreview.location.id
      })

      return addPartialCallRequest()
    })
  ))

  setAAR = createEffect(() => this.actions$.pipe(
    ofType(SET_AAR_ADDRESS),
    map((action: ReturnType<typeof setAARAddress>) => {
      this.taggingService.setClickEvent(
        events.towTo.DESTINATION_AAR_SELECT,
        events.towTo.DESTINATION_PAGE_TYPE,
        {
          params: {
            itemName: action.payload.location.name,
            itemId: action.payload.location.id
          }
        }
      )
      this.adobeEventService.sendEvent({
        eventName: AdobeEventTypes.CTA,
        eventValue: events.towTo.DESTINATION_AAR_SELECT,
      }, null, {
        location_name: action.payload.location.name,
        location_code: action.payload.location.id
      })
    }),
    debounceTime(500),
    tap(() => (window as any).aaa_gtm_prod?.push({ itemName: null })), // set itemName to null after making the setAAR event call
    tap(() => window.scroll({ top: 0, left: 0, behavior: 'smooth' }))
  ), { dispatch: false })

  updateTowDestination = createEffect(() => this.actions$.pipe(
    ofType(CONFIRM_TOW_DISTANCE),
    withLatestFrom(
      this.store$.pipe(select(selectIsTowLocationPreviewValid)),
    ),
    filter(([_, isPreviewValid]) => isPreviewValid),
    withLatestFrom(
      this.store$.pipe(select(selectTowLocationPreview)),
    ),
    map(([_, preview]) => requestSetTowDestination({
      payload: preview.location
    }))
  ))

  skipTowDestination$ = createEffect(() => this.actions$.pipe(
    ofType(CONFIRM_TOW_DISTANCE),
    withLatestFrom(
      this.store$.pipe(select(selectIsTowLocationPreviewValid)),
      this.store$.pipe(select(selectIsTowLocationValid)),
    ),
    filter(([_, isPreviewValid, isValid]) => !isPreviewValid && isValid),
    map(() => this.router.navigate([], {
      queryParams: {
        step: StepTypes.TOWING,
        section: TowSections.PASSENGERS
      }
    })
    )
  ), { dispatch: false })

  handleTowDestinationCoordinates$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GET_TOW_DESTINATION_COORDINATES),
      switchMap((action: PayloadedAction) => this._geocodeService.getLocationFromAddress(action.payload).pipe(
        map((geo) => setTowDestinationCoordinates({
          payload: {
            lat: geo[0].geometry.location.lat(),
            lng: geo[0].geometry.location.lng(),
          },
        }))
      ))
    )
  )

  checkTowingDistanceAndBenefits$ = createEffect(() => this.actions$.pipe(
    ofType<ReturnType<typeof checkTowDistance>>(CHECK_TOW_DISTANCE),
    withLatestFrom(
      this.store$.pipe(select(selectHasTowClubSettings)),
      this.store$.pipe(select(selectClubTowSettingByMemberLevel)),
      this.store$.pipe(select(selectRoutesDistance)),
      this.store$.pipe(select(selectModeConfiguration)),
      this.store$.pipe(select(selectMaxTowMileLimit)),
      this.store$.pipe(select(selectShownAARs)),
      this.store$.pipe(select(selectIsEVstation)),
      this.store$.pipe(select(selectActiveTowLimitRestriction)),
      this.store$.pipe(select(selectTowLimitAdvisories)),
      this.store$.pipe(select(selectLocationClub)),
      this.store$.pipe(select(selectBreakdownLocationDetails)),
    ),
    switchMap((params) => of(params).pipe(
      mergeMap(([action, hasTowSettings, memberLevel, routesDistance, modeConfiguration, eligibilityTowLimit, shownAars, isEvStation, towLimitRestriction, towLimitAdvisories, clubCode, breakdownDetails]) => {
        const { breakdownCoordinates, towLocation, destinationId } = action.payload;

        const towDestinationDistance = this.locationUtils.getDistance(routesDistance, breakdownCoordinates, {
          latitude: towLocation.lat,
          longitude: towLocation.lng
        })
        const distance = towDestinationDistance ? towDestinationDistance : calculateTowingDistance(breakdownCoordinates, towLocation)
        const boundaryMileage = hasTowSettings && memberLevel?.distance ? memberLevel.distance : null

        // Two gates, in order: the tow-benefits rules (member / club member-level
        // / RAP) and the peak-volume towLimit. A hard block from either skips
        // /tow-service-availability (DRRWEB-3329); the benefits one wins when both
        // apply. A soft warning fires the request alongside the dialog, deferred
        // until the user continues; with nothing to show it fires right away.
        const benefits = evaluateTowBenefits({
          isRapUser: this.rapService.isRapUser(),
          hasTowSettings,
          memberLevel,
          distance,
          towDestinationDistance,
          boundaryMileage,
          eligibilityTowLimit,
          modeConfiguration,
          shownAars,
          destinationId,
          isEvStation,
        })

        if (benefits.outcome === 'hardBlock') {
          this.tagBenefits(benefits.taggingEvent)
          return [benefits.dialog]
        }

        if (isTowLimitHardBlock(towLimitRestriction, distance)) {
          this.adobeEventService.sendEvent({
            eventName: AdobeEventTypes.SYSTEM,
            eventValue: events.towTo.DESTINATION_TOW_RESTRICTION_LIMIT,
          })
          return [openMessageDialog({
            payload: {
              type: MessageDialogTypes.TOW_RESTRICTION_MESSAGE,
              params: { messages: towLimitAdvisories },
            },
          })]
        }

        const payload = {
          clubCode,
          locationCode: breakdownDetails.locationCode,
          breakdownLatitude: Number(breakdownCoordinates.latitude),
          breakdownLongitude: Number(breakdownCoordinates.longitude),
          towDestinationLatitude: towLocation.lat,
          towDestinationLongitude: towLocation.lng,
        }

        if (benefits.outcome === 'softWarning') {
          this.tagBenefits(benefits.taggingEvent)
          return [benefits.dialog, requestTowServiceAvailability({ payload, meta: { deferred: true } })]
        }

        return [requestTowServiceAvailability({ payload })]
      }),
      catchError((error) => this.errorReportingService.notifyErrorObservable(error, notifySetTowDestinationFailure))
    ))
  ))

  handleSetTowingStep$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(SET_TOWING_STEP),
        withLatestFrom(
          this.store$.pipe(select(selectMemberShipLevel)),
          this.store$.pipe(select(selectMemberActiveVehicle)),
          this.store$.pipe(select(selectNeedsTow)),
          this.store$.pipe(select(selectTowAlertRV)),
          this.store$.pipe(select(createGetVehicleDetailsByModel))
        ),
        filter(
          ([action, membershipLevel, activeVehicle, needsTow, towAlertRV, getVehicleDetailsByModel]: [
            PayloadedAction<{ step: string }>,
            MembershipLevelsSettings,
            Vehicle,
            boolean,
            ServiceConfigurationSetting,
            MemoizedProjection
          ]) => {
            const vehicleDetails = getVehicleDetailsByModel.memoized(generateVehicleSlug({
              year: activeVehicle.year,
              make: activeVehicle.make,
              model: activeVehicle.model
            }))
            return needsTow &&
              Boolean(towAlertRV?.value) &&
              action.payload.step === TowSections.PASSENGERS &&
              isMotorhomeRV(vehicleDetails) &&
              membershipLevel &&
              RV_MEMBERSHIP_LEVELS.includes(membershipLevel)
          }
        ),
        tap(() => {
          // enforcing title to prevent concurrency issues in the analytic events
          this.titleService.setTitle(buildTitle(TITLE_SUBMIT(), this.rapService.isRapUser()))
          this.taggingService.setAutomatedEvent('RV Tow Alert Message displayed', events.submit.SUMMARY_PAGE_TYPE)
        }),
        map(([_, __, ___, ____, towAlertRV]) => openMessageDialog({
          payload: {
            type: MessageDialogTypes.CUSTOM,
            title: $localize`Alert`,
            content: towAlertRV.value,
            closeLabel: $localize`Continue`
          },
        }))
      ),
  )

  private tagBenefits(taggingEvent?: string) {
    if (taggingEvent) {
      this.taggingService.setAutomatedEvent(taggingEvent, events.towTo.DESTINATION_PAGE_TYPE)
    }
  }

  private resolveLocationRequestActions(action, location, breakdownLocationCoords, markerAddress?, previousMarker?) {
    const actions = []
    // consider as the same location when the BL is less than a tenth mile from the TL
    const sameLocationFromBreakdown = haversine(location, breakdownLocationCoords, {
      threshold: 0.1,
      unit: 'mile'
    })
    if (sameLocationFromBreakdown) {
      actions.push(openMessageDialog({
        payload: {
          type: MessageDialogTypes.SAME_ADDRESS_TOW_BL
        }
      }))
      // rollback the marker when its dropped at the same location from BL
      if (previousMarker) {
        actions.push(setTowDestinationMarker({
          payload: {
            ...action.payload,
            name: NON_AAR_TOWING_NAMES.CUSTOM,
            isAar: false,
            address: markerAddress
          }
        }))
        actions.push(setTowDestinationMarker({
          payload: previousMarker
        }))
      }
    } else {
      const locationPayload = (name) => ({
        ...location,
        name,
        isAar: false
      })
      const markerPayload = (name, address, lat, lng) => ({
        name,
        isAar: false,
        address,
        lat,
        lng
      })
      actions.push(completeAddressLookup({
        payload: {
          location: locationPayload(NON_AAR_TOWING_NAMES.CUSTOM),
          marker: markerPayload(
            NON_AAR_TOWING_NAMES.CUSTOM,
            action.payload.address,
            Number(location.latitude),
            Number(location.longitude))
        }
      }))
    }

    if (!this.rapService.isRapUser()) {
      actions.push(requestSearchArea({
        payload: {
          center: {
            latitude: location.latitude,
            longitude: location.longitude,
          },
          ...(action.payload.limit ? { limit: action.payload.limit } : {})
        }
      }))
    }
    return actions
  }
}

export const __TEST__ = {
  handleTowDestination
}

const RV_MEMBERSHIP_LEVELS = [
  MembershipLevelsSettings.RV,
  MembershipLevelsSettings.PREMIERRV,
]
